import React, { useState, useEffect } from 'react';
import {
  Box, Container, Grid, Paper, Typography, Divider, Button,
  CircularProgress, Drawer, Stepper, Step, StepLabel,
  TextField, useTheme, useMediaQuery, Snackbar, Alert, Skeleton, Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import ReactPlayer from 'react-player';
import { useParams, useNavigate } from 'react-router-dom';
// import Navbar from '../components/Navbar';
// import Footer from '../components/Footer';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe('pk_test_51HxxxxxxYOUR_KEYxxxxx');
const STEPS = ['Shipping', 'Payment', 'Confirmation'];

/* ─────────────────────────────────────────────────────────────────────────── */

export default function ProductDetailPage() {
  const { id }          = useParams();
  const navigate        = useNavigate();
  const theme           = useTheme();
  const mdUp            = useMediaQuery(theme.breakpoints.up('md'));
  const [product, l, e] = useProduct(id);

  const [drawerOpen, setDrawer]   = useState(false);
  const [activeStep, setStep]     = useState(0);
  const [shipping, setShipping]   = useState({ name:'',address:'',city:'',zip:'',country:'' });
  const [shipErr, setShipErr]     = useState({});
  const [processing, setProc]     = useState(false);
  const [snack, setSnack]         = useState('');

  if (l) return <SkeletonState />;
  if (e) return <ErrorState msg={e} />;

  /* ───────────────────────────────────────────────────────────────────────── */

  return (
    <>
      {/* <Navbar /> */}

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        <Button onClick={() => navigate('/shop')} size="small" sx={{ mb: 2 }}>
          ← Back to shop
        </Button>

        {/* Main grid */}
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Media + info */}
          <Grid item xs={12} md={8}>
            <Paper elevation={1} sx={{
              p:{ xs:2, md:4 }, borderRadius:3,
              backdropFilter:'blur(8px)', border:`1px solid ${theme.palette.divider}`
            }}>
              <Grid container spacing={4}>
                {/* Media column */}
                <Grid item xs={12} md={6}>
                  <Box component="img" src={product.image_url} alt={product.name}
                       sx={{ width:'100%', objectFit:'contain', borderRadius:2, bgcolor:'#fafafa' }}/>
                  {product.video_url && (
                    <Box mt={3} sx={{ position:'relative', pt:'56.25%' }}>
                      <ReactPlayer url={product.video_url} controls width="100%" height="100%"
                                   style={{ position:'absolute', top:0, left:0, borderRadius:8 }}/>
                    </Box>
                  )}
                </Grid>

                {/* Details column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h3" fontWeight={700} gutterBottom>{product.name}</Typography>

                  {/* NEW made badge */}
                  <Chip
                    label={product.made}
                    color={product.made.toLowerCase().includes('lemda') ? 'primary' : 'default'}
                    size="small" sx={{ mb: 2 }}
                  />

                  <Typography variant="body2" color="text.secondary" gutterBottom>{product.category}</Typography>
                  <Typography variant="h4" fontWeight={800} color="primary" sx={{ my:1 }}>
                    ${product.price.toFixed(2)}
                  </Typography>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="body1" color="text.secondary" sx={{ whiteSpace:'pre-line' }}>
                    {product.description}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Purchase card */}
          <Grid item xs={12} md={4}>
            <PurchaseCard
              price={product.price} mdUp={mdUp}
              onBuy={() => { setDrawer(true); setStep(0); }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Checkout drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawer(false)}
              PaperProps={{ sx:{ width:{ xs:'100%', sm:420 }, backdropFilter:'blur(6px)' }}}>
        <Box sx={{ p:{ xs:3, sm:4 } }}>
          <Typography variant="h6" gutterBottom>Checkout</Typography>

          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb:3 }}>
            {STEPS.map(lbl => <Step key={lbl}><StepLabel>{lbl}</StepLabel></Step>)}
          </Stepper>

          {/* Steps */}
          {activeStep === 0 && (
            <ShippingForm data={shipping} errors={shipErr} onChange={setShipping}
                          onContinue={()=>{
                            const errs={}; Object.entries(shipping).forEach(([k,v])=>!v.trim()&&(errs[k]='Required'));
                            setShipErr(errs); if(!Object.keys(errs).length) setStep(1);
                          }}/>
          )}

          {activeStep === 1 && (
            <Elements stripe={stripePromise}>
              <PaymentForm amount={product.price} processing={processing} setProcessing={setProc}
                           onSuccess={()=>setStep(2)}/>
            </Elements>
          )}

          {activeStep === 2 && (
            <Box textAlign="center">
              <Typography variant="h5" gutterBottom>Thanks for your purchase! 🎉</Typography>
              <Typography color="text.secondary">Your order is confirmed. A receipt is on its way.</Typography>
              <Button variant="contained" fullWidth sx={{ mt:4 }}
                      onClick={() => { setDrawer(false); navigate('/orders'); }}>
                View my orders
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* <Footer /> */}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={()=>setSnack('')}
                anchorOrigin={{ vertical:'bottom', horizontal:'center' }}>
        <Alert severity="error" onClose={()=>setSnack('')}>{snack}</Alert>
      </Snackbar>
    </>
  );
}

/* ───────────────────────── helper components (unchanged except badge) ──── */
function PurchaseCard({ price, mdUp, onBuy }) {
  const theme = useTheme();
  return (
    <Paper elevation={3} component={motion.div}
           initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ duration:.25 }}
           sx={{
             p:{ xs:3, md:4 }, borderRadius:3,
             position: mdUp ? 'sticky' : 'static', top: mdUp ? theme.spacing(14) : 'auto',
             bgcolor:'background.paper'
           }}>
      <Typography variant="h6" gutterBottom>Purchase</Typography>
      <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
        ${price.toFixed(2)}
      </Typography>
      <Button variant="contained" size="large" fullWidth onClick={onBuy}>Buy Now</Button>
    </Paper>
  );
}

function ShippingForm({ data, errors, onChange, onContinue }) {
  const handle = k => e => onChange({ ...data, [k]: e.target.value });
  return (
    <Box component="form" noValidate>
      {['name','address','city','zip','country'].map(key => (
        <TextField key={key} label={key.charAt(0).toUpperCase()+key.slice(1)}
                   fullWidth sx={{ mb:2 }} value={data[key]} onChange={handle(key)}
                   error={!!errors[key]} helperText={errors[key]}/>
      ))}
      <Button variant="contained" fullWidth onClick={onContinue}>Continue</Button>
    </Box>
  );
}

function PaymentForm({ amount, processing, setProcessing, onSuccess }) {
  const stripe = useStripe(); const elements = useElements();
  const pay = async () => {
    if(!stripe||!elements) return;
    setProcessing(true);
    const { error } = await stripe.createToken(elements.getElement(CardElement));
    if(error){ alert(error.message); setProcessing(false); return; }
    setTimeout(()=>{ setProcessing(false); onSuccess(); },1200); // fake charge
  };
  return (
    <Box>
      <Typography gutterBottom>Total: <strong>${amount.toFixed(2)}</strong></Typography>
      <Box sx={{ border:'1px solid',borderColor:'divider',borderRadius:2,p:2,mb:3 }}>
        <CardElement options={{ hidePostalCode:true }}/>
      </Box>
      <Button variant="contained" fullWidth disabled={!stripe||processing} onClick={pay}>
        {processing ? <CircularProgress size={22}/> : 'Pay now'}
      </Button>
    </Box>
  );
}

function useProduct(id){
  const [p,setP]=useState(null),[l,setL]=useState(true),[e,setE]=useState(null);
  useEffect(()=>{
    let live=true;
    fetch('https://lemda.co.uk/web_api/products.php')
      .then(r=>{if(!r.ok)throw new Error(r.status);return r.json()})
      .then(arr=>{if(!live)return; const f=arr.find(x=>x.id===+id); f?setP(f):setE('Not found');})
      .catch(err=>live&&setE(err.message)).finally(()=>live&&setL(false));
    return()=>{live=false};
  },[id]); return [p,l,e];
}
const SkeletonState=()=>(
  <>
    {/* <Navbar/> */}
    <Container sx={{ py:10 }}><Skeleton variant="rectangular" height={380}/></Container>
    {/* <Footer/> */}
  </>
);
const ErrorState=({msg})=>(
  <>
    {/* <Navbar/> */}
    <Container sx={{ py:10 }}><Alert severity="error">{msg}</Alert></Container>
    {/* <Footer/> */}
  </>
);
